import React, { FC, useEffect } from 'react';
import { connect } from 'react-redux';
import _isEmpty from 'lodash/isEmpty';
import Head from 'next/head';
import Script from 'next/script';
import type { Metadata } from 'types';

import { flattenUserGroup, isValidUrl } from '../utils';
import { initGTM } from '../utils/analyticsGTM';

const Meta: FC<Metadata> = (props) => {
  const {
    TITLE,
    META_DESCRIPTION,
    META_KEYWORDS,
    OG_TITLE,
    OG_META_DESCRIPTION,
    OG_IMAGECOMPANY,
    OG_IMAGE,
    PUBLISHED_TIME,
    MODIFIED_TIME,
    UPDATED_TIME,
    SQUARE_WEB_SDK_CDN,
    host,
    url,
    noIndex,
    indexAndNoFollow,
    relCanonical,
    pageCategory,
    structuredData,
    userGroup,
    indexAndFollow,
    useStaticOgImage = true,
  } = props;

  useEffect(() => {
    if (!window.GA_INITIALIZED) {
      initGTM();
      window.GA_INITIALIZED = true;
    }
    if (/MSIE \d|Trident.*rv:/.test(navigator.userAgent)) {
      window.location.href = `microsoft-edge:${window.location.href}`;
      setTimeout(() => {
        window.location.href = 'https://go.microsoft.com/fwlink/?linkid=2135547';
      }, 1);
    }
  }, []);

  function getTwitterImageSrcContent() {
    if (OG_IMAGECOMPANY) {
      return OG_IMAGECOMPANY;
    }
    if (OG_IMAGE) {
      if (isValidUrl(OG_IMAGE)) return OG_IMAGE;
      else return `${host}/assets/${OG_IMAGE}`;
    }
    return 'https://static.zippia.com/assets/zippia-og-image.png';
  }

  const filteredStructuredData = Array.isArray(structuredData)
    ? structuredData?.filter((item) => !_isEmpty(item))
    : structuredData;

  return (
    <Head>
      <title>{TITLE}</title>
      {META_DESCRIPTION && <meta name="description" content={META_DESCRIPTION} />}

      <meta name="author" content="" />
      {META_KEYWORDS && <meta name="keywords" content={META_KEYWORDS} />}

      {/* Open Graph data */}
      <meta property="og:title" content={OG_TITLE} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={host + url} />
      <meta property="og:description" content={OG_META_DESCRIPTION} />

      {/* CMP Cluster Modified Dates Meta contents */}
      {PUBLISHED_TIME && MODIFIED_TIME && UPDATED_TIME && (
        <>
          <meta property="article:published_time" content={PUBLISHED_TIME} />
          <meta property="article:modified_time" content={MODIFIED_TIME} />
          <meta property="og:updated_time" content={UPDATED_TIME} />
        </>
      )}

      {/* Twitter cards */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:site" content="@ZippiaInc" />
      <meta name="twitter:title" content={OG_TITLE} />
      <meta name="twitter:url" content={host + url} />
      <meta name="twitter:description" content={OG_META_DESCRIPTION} />
      <meta charSet="utf8" />
      {/* Twitter Summary card images must be at least 120x120px */}
      <meta name="twitter:image:src" content={getTwitterImageSrcContent()} />

      {/* Facebook APP_ID */}
      <meta property="fb:app_id" content="508633732650088" />
      <meta
        property="og:image"
        content={
          useStaticOgImage
            ? 'https://static.zippia.com/assets/zippia-og-image.png'
            : getTwitterImageSrcContent()
        }
      />

      {/* Set noindex or nofollow tags */}
      {noIndex && <meta name="robots" content="noindex, follow" />}
      {!noIndex && indexAndNoFollow && <meta name="robots" content="index, nofollow" />}
      {!noIndex && indexAndFollow && <meta name="robots" content="index, follow" />}

      {relCanonical && !noIndex && <link rel={relCanonical.rel} href={relCanonical.href} />}
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: ` var pageCategory = '${pageCategory}';`,
        }}
      />

      {/*
        Keep userGroup  variables in the head before the GTM script because these will be used by the GTM (although this is
        not guaranteed that GTM will not be loaded before this script but still keeping it before GTM script for most of the cases.
    */}
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: ` var userGroup = '${userGroup}';`,
        }}
      />
      {/* Structured Data */}
      {filteredStructuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(filteredStructuredData) }}
        />
      )}

      <Script src="https://accounts.google.com/gsi/client" strategy="lazyOnload" />
      <Script src="https://www.google.com/recaptcha/api.js" strategy="lazyOnload" />

      {/* Sandbox CDN */}
      {SQUARE_WEB_SDK_CDN && <script src={SQUARE_WEB_SDK_CDN} async defer />}
    </Head>
  );
};

Meta.defaultProps = {
  userGroup: '',
};

const mapStateToProps = ({ locals }) => {
  let userGroup = '';
  if (locals && locals.userGroup) {
    userGroup = flattenUserGroup(locals.userGroup);
  }
  return { userGroup };
};

export default connect(mapStateToProps, null)(Meta);
